import { Button, IconButton, SelectInput, SelectInputItem } from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePolosDropdown } from '../../../hooks/usePolosDropdown';
import type { EnrollmentModalData } from '../../../types/enrollment';
import { EnrollmentStatusBadge } from '../../atoms/EnrollmentStatusBadge';

export interface ChangePoloModalProps {
  readonly enrollment: EnrollmentModalData | null;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (data: { enrollmentId: number; newPoloId: string }) => void;
  readonly isLoading?: boolean;
}

export function ChangePoloModal({
  enrollment,
  open,
  onClose,
  onConfirm,
  isLoading = false,
}: ChangePoloModalProps) {
  const { t } = useTranslation('personManagement');
  const { polos } = usePolosDropdown();

  const [newPoloId, setNewPoloId] = useState('');
  const [error, setError] = useState(false);

  const resetForm = () => {
    setNewPoloId('');
    setError(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleConfirm = () => {
    if (!enrollment) return;

    if (!newPoloId) {
      setError(true);
      return;
    }

    onConfirm({
      enrollmentId: enrollment.id,
      newPoloId,
    });
  };

  useEffect(() => {
    if (open) return;
    resetForm();
    const id = setTimeout(() => {
      document.body.style.removeProperty('pointer-events');
    }, 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resetForm]);

  return (
    <Dialog.Root open={open} onOpenChange={o => !o && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft ![animation:none]" />

        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none ![animation:none]"
          aria-describedby={undefined}
        >
          {enrollment && (
            <>
              {/* Header */}
              <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-xs py-mini">
                <Dialog.Title className="ts-heading-sm font-semibold text-ctx-content-title">
                  {t('poloModal.title')}
                </Dialog.Title>
                <IconButton
                  icon={XMarkIcon}
                  hierarchy="tertiary"
                  size="sm"
                  ariaLabel={t('actions.cancel')}
                  onClick={handleClose}
                />
              </div>

              {/* Body */}
              <div className="flex flex-col gap-xxs overflow-y-auto px-xs py-mini">
                {/* Enrollment info card */}
                <div className="flex flex-col gap-mini rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs">
                  <div className="flex flex-wrap items-center gap-xs">
                    <span className="ts-heading-xs !font-medium text-ctx-content-title">
                      {enrollment.personName}
                    </span>
                    <EnrollmentStatusBadge status={enrollment.status} />
                  </div>
                  <div className="grid grid-cols-2 gap-xs sm:grid-cols-3">
                    <div className="flex flex-col gap-quark">
                      <span className="ts-subtitle-xxs text-ctx-content-base">
                        {t('table.columns.course')}
                      </span>
                      <span className="ts-caption-xxs text-ctx-content-placeholder line-clamp-2">
                        {enrollment.courseName}
                      </span>
                    </div>
                    <div className="flex flex-col gap-quark">
                      <span className="ts-subtitle-xxs text-ctx-content-base">
                        {t('table.columns.className')}
                      </span>
                      <span className="ts-caption-xxs text-ctx-content-placeholder line-clamp-2">
                        {enrollment.className}
                      </span>
                    </div>
                    <div className="flex flex-col gap-quark">
                      <span className="ts-subtitle-xxs text-ctx-content-base">
                        {t('poloModal.currentPolo')}
                      </span>
                      <span className="ts-caption-xxs text-ctx-content-placeholder">
                        {enrollment.poleName}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-xs sm:grid-cols-3">
                    <div className="flex flex-col gap-quark">
                      <span className="ts-subtitle-xxs text-ctx-content-base">CPF</span>
                      <span className="ts-caption-xxs text-ctx-content-placeholder">
                        {enrollment.personCpf}
                      </span>
                    </div>
                    <div className="flex flex-col gap-quark">
                      <span className="ts-subtitle-xxs text-ctx-content-base">E-mail</span>
                      <span className="ts-caption-xxs text-ctx-content-placeholder truncate">
                        {enrollment.personEmail}
                      </span>
                    </div>
                    <div className="flex flex-col gap-quark">
                      <span className="ts-subtitle-xxs text-ctx-content-base">
                        {t('enrollmentDetail.enrollmentDate')}
                      </span>
                      <span className="ts-caption-xxs text-ctx-content-placeholder">
                        {new Date(enrollment.enrollmentDate).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* New polo dropdown */}
                <div className="flex flex-col gap-quark">
                  <SelectInput
                    size="lg"
                    label={t('poloModal.newPolo')}
                    value={newPoloId || 'none'}
                    onValueChange={v => {
                      setNewPoloId(v === 'none' ? '' : v);
                      setError(false);
                    }}
                    custom={{ content: 'z-[9999]' }}
                  >
                    <SelectInputItem value="none" custom={{ root: 'text-nowrap' }}>
                      {t('poloModal.selectPolo')}
                    </SelectInputItem>
                    {polos.map(p => (
                      <SelectInputItem key={p.id} value={p.id} custom={{ root: 'text-nowrap' }}>
                        {p.name}
                      </SelectInputItem>
                    ))}
                  </SelectInput>
                  {error && (
                    <span className="ts-caption-xxs text-base-danger-500">
                      {t('poloModal.poloRequired')}
                    </span>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-mini border-t-hairline border-ctx-layout-border px-md py-xs">
                <Button hierarchy="secondary" size="lg" onClick={handleClose}>
                  {t('actions.cancel')}
                </Button>
                <Button
                  size="lg"
                  onClick={handleConfirm}
                  isLoading={isLoading}
                  disabled={isLoading}
                >
                  {t('actions.save')}
                </Button>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
